import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";

type Kind = "success" | "failure" | "pending";

export function PaymentResult({ kind }: { kind: Kind }) {
  const nav = useNavigate();
  const { claims } = useAuth();
  const [params] = useSearchParams();
  const paymentId = params.get("ref") ?? params.get("external_reference");
  const [status, setStatus] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);

  useEffect(() => {
    if (!claims.tenantId || !paymentId) return;
    return onSnapshot(
      doc(db, "tenants", claims.tenantId, "payments", paymentId),
      (snap) => {
        const d = snap.data();
        if (d) {
          setStatus(d.status as string);
          setAmount(typeof d.amount === "number" ? d.amount : null);
        }
      }
    );
  }, [claims.tenantId, paymentId]);

  const title =
    kind === "success" ? "✓ Pago aprobado" : kind === "failure" ? "Pago rechazado" : "Pago en proceso";
  const subtitle =
    kind === "success"
      ? "Recibimos tu pago. Gracias por tu compra."
      : kind === "failure"
      ? "Tu pago no se completó. Puedes intentar de nuevo."
      : "Estamos esperando confirmación de Mercado Pago. Te avisamos cuando se acredite.";

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col items-center gap-6 text-center">
        <div
          className={`grid h-16 w-16 place-items-center rounded-full ${
            kind === "success" ? "bg-leaf-100" : kind === "failure" ? "bg-red-100" : "bg-amber-100"
          }`}
        >
          {kind === "success" && (
            <svg viewBox="0 0 24 24" className="h-8 w-8 text-leaf-700" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7" />
            </svg>
          )}
          {kind === "failure" && (
            <svg viewBox="0 0 24 24" className="h-8 w-8 text-red-700" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {kind === "pending" && (
            <svg viewBox="0 0 24 24" className="h-8 w-8 text-amber-700" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
            </svg>
          )}
        </div>

        <header>
          <h1 className="font-display text-2xl font-semibold text-leaf-950">{title}</h1>
          <p className="mt-2 text-sm text-leaf-700">{subtitle}</p>
        </header>

        {amount !== null && (
          <div className="rounded-2xl bg-white p-4 ring-1 ring-leaf-100">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Monto</p>
            <p className="font-display text-2xl font-semibold text-leaf-950">
              ${amount.toLocaleString("es-CL")}
            </p>
            {status && (
              <p className="mt-1 text-[10px] uppercase tracking-wider text-leaf-500">
                Estado: {status}
              </p>
            )}
          </div>
        )}

        <button onClick={() => nav("/")} className="btn-primary">
          Volver al inicio
        </button>
      </div>
    </main>
  );
}
